import React, { useState, useRef, useEffect } from 'react';
import css from 'styled-jsx/css';
import { colors, dimensions } from '../common/Theme';
import classnames from 'classnames';
import Card from '../common/layout/Card';
import { breakpoints } from '../style/breakpoints';
import { guidGenerator } from '../../services/Utils';
import PropTypes from 'prop-types';

const dynamicStyles = css.resolve`
  .testimony-title {
    color: ${colors.ywgNavy};
    font-size: 1.5rem;
    margin: 50px 60px 10px;
    padding: 0;
    text-align: center;
    max-width: 370px;
  }
  .carousel__background {
    position: relative;
    margin: auto;
    padding: 0 0 0 20px;
    max-width: ${dimensions.ywgSectionMaxWidth};
    background: none;
  }
  .carousel__background::after {
    content: '';
    display: block;
    width: 100%;
    height: 302px;
    background-color: #e8ebef;
    background-color: ${colors.ywgVeryLightBlue};
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: -1;
  }
  .carousel__container {
    min-width: 100%;
    overflow: hidden;
  }
  .carousel {
    display: flex;
    position: relative;
    bottom: -20px;
    width: 100%;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    padding-top: 30px;
    padding-bottom: 60px;
  }
  @media ${breakpoints.md} {
    .carousel {
      padding-top: 0px;
      padding-bottom: 40px;
    }
  }

  .carousel__arrows {
    position: relative;
    width: 70%;
    display: flex;
    justify-content: space-evenly;
    margin: auto;
    margin-bottom: 25px;
  }

  @media ${breakpoints.md} {
    .carousel__container {
      margin-right: calc(-100vw / 2 + ${dimensions.ywgSectionMaxWidth} / 2);
    }
    .carousel__arrows {
      width: 400px;
      left: calc(25% + 60px);
      padding-top: 20px;
      margin-bottom: 0px;
    }
  }
  @media ${breakpoints.md} {
    .carousel__background::after {
      height: 110%;
      bottom: auto;
      top: 90px;
      left: 785px;
      width: calc(50vw - 190px);
    }
  }
  .carousel__avatar {
    position: absolute;
    top: 0;
    margin: 0;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    border-radius: 50%;
  }
  @media ${breakpoints.md} {
    .carousel__avatar {
      width: 140px;
    }
  }
  .testimony__container {
    position: relative;
    display: flex;
    flex-shrink: 0;
    scroll-snap-align: start;
  }
  .carousel__testimony {
    padding: 45px 15px 40px;
    margin: 0 15px 0 0;
    width: 240px;
    height: 80%;
    box-sizing: content-box;
    position: relative;
  }
  @media ${breakpoints.md} {
    .carousel__testimony {
      padding: 110px 90px 124px;
      width: 330px;
      height: auto;
      margin-top: 70px;
    }
  }

  .testimony__blank {
    min-width: 525px;
  }
  .testimony-text {
    position: relative;
    margin: 0;
    padding: 30px 0 0;
    overflow-y: hidden;
    color: ${colors.ywgNavyLight};
  }
  .testimony-text::before {
    position: absolute;
    top: 0;
    width: 21px;
    height: 17px;
    content: '';
    background: url(/static/images/guillemets-default.svg) 100%/100% no-repeat;
    filter: brightness(0.5) sepia(1) hue-rotate(-36deg) saturate(4);
  }
  .testimony-author {
    margin: 20px 0 0 0;
    padding: 0;
    color: ${colors.ywgNavyLight};
  }

  .testimony-text--active,
  .testimony-author--active {
    color: #ffffff;
  }
  .testimony-text--active::before {
    filter: none;
  }

  @media ${breakpoints.md} {
    .testimony-title {
      margin: 90px 0 10px;
      text-align: left;
      font-size: 2.5rem;
    }

    .testimony-text {
      padding-top: 60px;
    }

    .testimony-text::before {
      width: 52px;
      height: 42px;
    }
    .testimony-author {
      margin: 30px 0 0 0;
    }
  }

  @media screen and (min-width: ${dimensions.ywgSectionMaxWidth}) {
    :global(.slick-slider) {
      margin-right: calc(-100vw / 2 + ${dimensions.ywgSectionMaxWidth} / 2);
      padding-bottom: 68px;
    }
  }
`;

const arrowStyles = css`
  .arrow {
    display: block;
    width: 80%;
    background-color: transparent;
    border: none;
    outline: none;
  }
  .arrow--next {
    transform: rotate(180deg);
  }
`;

const separatorStyles = css`
  .separator {
    position: relative;
    top: 6px;
    background-color: #c9d3de;
    width: 2px;
    transform: translateY(-25%);
  }
`;

const Arrow = ({ direction, className, ...props }) => {
  const classOverrides = classnames('arrow', className, {
    'arrow--next': direction === 'next',
    'arrow--prev': direction === 'prev'
  });
  return (
    <button {...props} className={classOverrides}>
      <img src="/static/images/arrow.svg" alt="arrow" />
      <style jsx>{arrowStyles}</style>
    </button>
  );
};

Arrow.propTypes = {
  direction: PropTypes.oneOf(['next', 'prev']),
  className: PropTypes.string
};

const Separator = ({ className }) => {
  const classOverrides = classnames('separator', className);
  return (
    <div className={classOverrides}>
      {' '}
      <style jsx>{separatorStyles}</style>
    </div>
  );
};

const Carousel = props => {
  const [offset, setOffset] = useState();
  const [testimonyWidth, setTestimonyWidth] = useState();
  const [rootMarginObj, setRootMarginObj] = useState();
  const [loadImage, setLoadImage] = useState(false);
  const testimoniesRef = useRef([]);
  const carouselRef = useRef();
  const dimensions = {
    desktopMinWidth: 960,
    mobileTestimonyWidth: 255,
    desktopTestimonyWidth: 525,
    desktopRootMargin: '0px -35% 0px 0px',
    mobileRootMargin: '0px'
  };

  useEffect(() => {
    setLoadImage(true);
    setOffset(carouselRef.current.offsetLeft);
  }, []);

  useEffect(() => {
    screen.width < dimensions.desktopMinWidth
      ? (setTestimonyWidth(dimensions.mobileTestimonyWidth),
        setRootMarginObj(dimensions.mobileRootMargin))
      : (setTestimonyWidth(dimensions.desktopTestimonyWidth),
        setRootMarginObj(dimensions.desktopRootMargin));
  }, [dimensions]);

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio === 1) {
              entry.target.firstChild.classList.add('card--secondary');
              entry.target.firstChild.children[1].classList.add(
                'testimony-text--active'
              );
              entry.target.firstChild.children[2].classList.add(
                'testimony-author--active'
              );
            }
            if (entry.intersectionRatio < 1 && entry.intersectionRatio > 0) {
              entry.target.firstChild.classList.remove('card--secondary');
              entry.target.firstChild.children[1].classList.remove(
                'testimony-text--active'
              );
              entry.target.firstChild.children[2].classList.remove(
                'testimony-author--active'
              );
            }
          });
        },
        {
          root: carouselRef.current,
          rootMargin: rootMarginObj,
          threshold: 1
        }
      );
      testimoniesRef.current.forEach(testimony => observer.observe(testimony));
    }
  }, [rootMarginObj]);

  const slides = () => {
    return props.testimonies.map((testimony, index) => {
      return (
        <div
          key={guidGenerator()}
          className={classnames(
            'testimony__container',
            dynamicStyles.className
          )}
          ref={testimony => (testimoniesRef.current[index] = testimony)}
        >
          <Card
            className={classnames(
              'carousel__testimony',
              dynamicStyles.className
            )}
          >
            <img
              className={classnames(
                'carousel__avatar',
                dynamicStyles.className
              )}
              alt={testimony.avatar.alt}
              src={loadImage ? testimony.avatar.srccarousel__avatar : null}
              srcSet={loadImage ? testimony.avatar.srcSet : null}
              sizes="(max-width: 960px) 60px, 140px"
            />
            <p
              className={classnames('testimony-text', dynamicStyles.className)}
            >
              «&nbsp;{testimony.text}&nbsp;»
            </p>
            <p
              className={classnames(
                'testimony-author',
                dynamicStyles.className
              )}
            >
              <strong>{testimony.name} </strong> -{' '}
              <span>{testimony.title}</span>
            </p>
          </Card>
        </div>
      );
    });
  };

  const moveCarouselTo = direction => {
    switch (direction) {
      case 'right':
        offset < 0
          ? null
          : ((carouselRef.current.scrollLeft += testimonyWidth),
            setOffset(carouselRef.current.offsetLeft));
        break;
      case 'left':
        offset < 0
          ? null
          : ((carouselRef.current.scrollLeft += -testimonyWidth),
            setOffset(carouselRef.current.offsetLeft));
        break;
    }
  };

  return (
    <section
      id="temoignage"
      className={classnames('carousel__background', dynamicStyles.className)}
    >
      <h2 className={classnames('testimony-title', dynamicStyles.className)}>
        Des histoires à raconter
      </h2>
      <div
        className={classnames('carousel__container', dynamicStyles.className)}
      >
        <div
          className={classnames('carousel', dynamicStyles.className)}
          ref={carouselRef}
        >
          {slides()}
          <div
            className={classnames('testimony__blank', dynamicStyles.className)}
          ></div>
          <div
            className={classnames('testimony__blank', dynamicStyles.className)}
          ></div>
        </div>
      </div>
      <div className={classnames('carousel__arrows', dynamicStyles.className)}>
        <Arrow direction="prev" onClick={() => moveCarouselTo('left')} />
        <Separator />
        <Arrow direction="next" onClick={() => moveCarouselTo('right')} />
      </div>
      {dynamicStyles.styles}
    </section>
  );
};

export default Carousel;
